import { Warning } from '@mui/icons-material'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import { useState, useEffect, useCallback } from 'react'

interface CancelAppointmentDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (sendEmails: boolean) => void
  appointmentId?: string
  customerName?: string
  scheduleStartTime?: string | null
}

export const CancelAppointmentDialog = ({
  open,
  onClose,
  onConfirm,
  appointmentId,
  customerName,
  scheduleStartTime,
}: CancelAppointmentDialogProps) => {
  // Calculate if scheduleStartTime is more than 48 hours in the past
  const getDefaultSendEmails = useCallback(() => {
    if (!scheduleStartTime) return true

    const now = new Date()
    const scheduleDate = new Date(scheduleStartTime)
    const hoursDifference = (now.getTime() - scheduleDate.getTime()) / (1000 * 60 * 60)

    // If more than 48 hours in the past, default to false
    return hoursDifference <= 48
  }, [scheduleStartTime])

  const [sendEmails, setSendEmails] = useState(getDefaultSendEmails())

  // Update sendEmails when dialog opens with a different scheduleStartTime
  useEffect(() => {
    if (open) {
      setSendEmails(getDefaultSendEmails())
    }
  }, [open, getDefaultSendEmails])

  const handleClose = () => {
    setSendEmails(getDefaultSendEmails()) // Reset to calculated default when closing
    onClose()
  }

  const handleConfirm = () => {
    onConfirm(sendEmails)
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Warning color="error" />
          <Typography component="span">Cancel Appointment</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to cancel this appointment?
          {customerName && (
            <>
              <br />
              <br />
              <strong>Customer:</strong> {customerName}
              <br />
              <strong>Appointment ID:</strong> {appointmentId}
            </>
          )}
          <br />
          <br />
          This action cannot be undone.
        </DialogContentText>
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmails}
                onChange={e => setSendEmails(e.target.checked)}
                color="primary"
              />
            }
            label="Send cancellation emails to customer and scout"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Keep Appointment
        </Button>
        <Button onClick={handleConfirm} color="error" variant="contained">
          Cancel Appointment
        </Button>
      </DialogActions>
    </Dialog>
  )
}
